"use client";

import { c as _c } from "react/compiler-runtime";
import type { ProfileActivityLog } from "@/entities/IProfile";
import type { CountlessPage } from "@/helpers/Types";
import { commonApiFetch } from "@/services/api/common-api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import ProfileActivityLogsFilter from "./filter/ProfileActivityLogsFilter";
import ProfileActivityLogsList from "./list/ProfileActivityLogsList";
import { convertActivityLogParams } from "@/helpers/profile-logs.helpers";
import { selectActiveGroupId } from "@/store/groupSlice";
import { ProfileActivityFilterTargetType, type ProfileActivityLogType, RateMatter } from "@/types/enums";
import { useSelector } from "react-redux";
import { QueryKey } from "../react-query-wrapper/ReactQueryWrapper";
import CommonCardSkeleton from "../utils/animation/CommonCardSkeleton";
import type { CommonSelectItem } from "../utils/select/CommonSelect";
import CommonTabs from "../utils/select/tabs/CommonTabs";
import CommonTablePagination from "../utils/table/paginator/CommonTablePagination";
export interface ActivityLogParams {
  readonly page: number;
  readonly pageSize: number;
  readonly logTypes: ProfileActivityLogType[];
  readonly matter: RateMatter | null;
  readonly targetType: ProfileActivityFilterTargetType;
  readonly handleOrWallet: string | null;
  readonly groupId: string | null;
}
const MATTER_TAB_ITEMS: CommonSelectItem<RateMatter | null>[] = [{
  label: "All",
  value: null,
  key: "all"
}, {
  label: "REP",
  value: RateMatter.REP,
  key: "rep"
}, {
  label: "NIC",
  value: RateMatter.NIC,
  key: "nic"
}];
const DIRECTION_TAB_ITEMS: CommonSelectItem<ProfileActivityFilterTargetType>[] = [{
  label: "All",
  value: ProfileActivityFilterTargetType.ALL,
  key: "all"
}, {
  label: "Incoming",
  value: ProfileActivityFilterTargetType.INCOMING,
  key: "incoming"
}, {
  label: "Outgoing",
  value: ProfileActivityFilterTargetType.OUTGOING,
  key: "outgoing"
}];
export interface ActivityLogParamsConverted {
  readonly page: string;
  readonly page_size: string;
  readonly log_type: string;
  include_incoming?: string | undefined;
  rating_matter?: string | undefined;
  profile?: string | undefined;
  target?: string | undefined;
  group_id?: string | undefined;
}
export default function ProfileActivityLogs(t0) {
  const $ = _c(79);
  const {
    initialParams,
    withFilters,
    withMatterFilter: t1,
    disableActiveGroup: t2,
    children
  } = t0;
  const withMatterFilter = t1 === undefined ? false : t1;
  const disableActiveGroup = t2 === undefined ? false : t2;
  const activeGroupId = useSelector(selectActiveGroupId);
  const [selectedFilters, setSelectedFilters] = useState(initialParams.logTypes);
  const [targetType, setTargetType] = useState(initialParams.targetType);
  const [currentPage, setCurrentPage] = useState(initialParams.page);
  const [matter, setMatter] = useState(initialParams.matter);
  let t3;
  if ($[0] !== initialParams.logTypes || $[1] !== initialParams.matter || $[2] !== initialParams.page || $[3] !== initialParams.targetType) {
    t3 = () => {
      setSelectedFilters(initialParams.logTypes);
      setTargetType(initialParams.targetType);
      setCurrentPage(initialParams.page);
      setMatter(initialParams.matter);
    };
    $[0] = initialParams.logTypes;
    $[1] = initialParams.matter;
    $[2] = initialParams.page;
    $[3] = initialParams.targetType;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== initialParams) {
    t4 = [initialParams];
    $[5] = initialParams;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== selectedFilters) {
    t5 = filter => {
      if (selectedFilters.includes(filter)) {
        setSelectedFilters(selectedFilters.filter(f => f !== filter));
      } else {
        setSelectedFilters([...selectedFilters, filter]);
      }
      setCurrentPage(1);
    };
    $[7] = selectedFilters;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const onFilter = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = target => {
      setTargetType(target);
      setCurrentPage(1);
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const onTargetType = t6;
  let t7;
  if ($[10] !== initialParams.logTypes) {
    t7 = m => {
      setMatter(m);
      setSelectedFilters(initialParams.logTypes);
      setCurrentPage(1);
    };
    $[10] = initialParams.logTypes;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const onMatterChange = t7;
  let t8;
  if ($[12] !== activeGroupId || $[13] !== currentPage || $[14] !== disableActiveGroup || $[15] !== initialParams.handleOrWallet || $[16] !== initialParams.pageSize || $[17] !== matter || $[18] !== selectedFilters || $[19] !== targetType) {
    t8 = convertActivityLogParams({
      params: {
        page: currentPage,
        pageSize: initialParams.pageSize,
        logTypes: selectedFilters,
        matter,
        targetType,
        handleOrWallet: initialParams.handleOrWallet,
        groupId: activeGroupId
      },
      disableActiveGroup: !!disableActiveGroup
    });
    $[12] = activeGroupId;
    $[13] = currentPage;
    $[14] = disableActiveGroup;
    $[15] = initialParams.handleOrWallet;
    $[16] = initialParams.pageSize;
    $[17] = matter;
    $[18] = selectedFilters;
    $[19] = targetType;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const [params, setParams] = useState(t8);
  let t9;
  if ($[21] !== activeGroupId || $[22] !== currentPage || $[23] !== disableActiveGroup || $[24] !== initialParams.handleOrWallet || $[25] !== initialParams.pageSize || $[26] !== matter || $[27] !== selectedFilters || $[28] !== targetType) {
    t9 = () => {
      setParams(convertActivityLogParams({
        params: {
          page: currentPage,
          pageSize: initialParams.pageSize,
          logTypes: selectedFilters,
          matter,
          targetType,
          handleOrWallet: initialParams.handleOrWallet,
          groupId: activeGroupId
        },
        disableActiveGroup: !!disableActiveGroup
      }));
    };
    $[21] = activeGroupId;
    $[22] = currentPage;
    $[23] = disableActiveGroup;
    $[24] = initialParams.handleOrWallet;
    $[25] = initialParams.pageSize;
    $[26] = matter;
    $[27] = selectedFilters;
    $[28] = targetType;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== activeGroupId || $[31] !== currentPage || $[32] !== initialParams.handleOrWallet || $[33] !== matter || $[34] !== selectedFilters || $[35] !== targetType) {
    t10 = [currentPage, selectedFilters, initialParams.handleOrWallet, targetType, activeGroupId, matter];
    $[30] = activeGroupId;
    $[31] = currentPage;
    $[32] = initialParams.handleOrWallet;
    $[33] = matter;
    $[34] = selectedFilters;
    $[35] = targetType;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  useEffect(t9, t10);
  let t11;
  if ($[37] !== params) {
    t11 = {
      queryKey: [QueryKey.PROFILE_LOGS, {
        ...params
      }],
      queryFn: async () => await commonApiFetch({
        endpoint: "profile-logs",
        params
      }),
      placeholderData: keepPreviousData
    };
    $[37] = params;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const {
    isLoading,
    data: logs
  } = useQuery(t11);
  let t12;
  if ($[39] !== isLoading || $[40] !== logs) {
    t12 = () => {
      if (isLoading) {
        return;
      }
      if (logs?.page === 1 && !logs.data.length) {
        setCurrentPage(1);
      }
    };
    $[39] = isLoading;
    $[40] = logs;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const t13 = logs?.page;
  let t14;
  if ($[42] !== isLoading || $[43] !== t13) {
    t14 = [t13, isLoading];
    $[42] = isLoading;
    $[43] = t13;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  useEffect(t12, t14);
  const t15 = `${initialParams.handleOrWallet ? "" : "tw-mt-2"} `;
  const t16 = `${withMatterFilter ? "tw-flex tw-w-full tw-flex-col tw-gap-y-4" : "tw-flex tw-w-full tw-flex-col tw-gap-y-4 min-[1200px]:tw-flex-row min-[1200px]:tw-items-start min-[1200px]:tw-justify-between min-[1200px]:tw-gap-x-6"}`;
  let t17;
  if ($[45] !== children) {
    t17 = children && <div className="tw-flex-none tw-whitespace-nowrap">{children}</div>;
    $[45] = children;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== initialParams.handleOrWallet || $[48] !== initialParams.logTypes || $[49] !== matter || $[50] !== onFilter || $[51] !== selectedFilters || $[52] !== targetType || $[53] !== withFilters || $[54] !== withMatterFilter) {
    t18 = withFilters && !withMatterFilter && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-3">{initialParams.handleOrWallet && <div className="tw-flex-shrink-0"><CommonTabs items={DIRECTION_TAB_ITEMS} activeItem={targetType} filterLabel="Filter direction" setSelected={onTargetType} size="sm" fill={false} /></div>}{matter === RateMatter.NIC && <div className="md:tw-ml-auto tw-flex-1 md:tw-flex-none md:tw-w-[17.5rem] min-[1200px]:tw-w-[18rem] min-[1400px]:tw-w-[20rem]"><ProfileActivityLogsFilter user={initialParams.handleOrWallet} options={initialParams.logTypes} selected={selectedFilters} setSelected={onFilter} /></div>}</div>;
    $[47] = initialParams.handleOrWallet;
    $[48] = initialParams.logTypes;
    $[49] = matter;
    $[50] = onFilter;
    $[51] = selectedFilters;
    $[52] = targetType;
    $[53] = withFilters;
    $[54] = withMatterFilter;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== initialParams.handleOrWallet || $[57] !== initialParams.logTypes || $[58] !== matter || $[59] !== onFilter || $[60] !== onMatterChange || $[61] !== selectedFilters || $[62] !== targetType || $[63] !== withFilters || $[64] !== withMatterFilter) {
    t19 = withMatterFilter && <div className="tw-flex tw-w-full tw-items-center tw-gap-3"><div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-3"><div className="tw-max-w-[12rem]"><CommonTabs items={MATTER_TAB_ITEMS} activeItem={matter} filterLabel="Filter matter" setSelected={onMatterChange} size="sm" /></div>{initialParams.handleOrWallet && <div className="tw-max-w-[16rem]"><CommonTabs items={DIRECTION_TAB_ITEMS} activeItem={targetType} filterLabel="Filter direction" setSelected={onTargetType} size="sm" /></div>}</div>{withFilters && matter === RateMatter.NIC && <div className="tw-ml-auto tw-w-[17.5rem] min-[1200px]:tw-w-[18rem] min-[1400px]:tw-w-[20rem]"><ProfileActivityLogsFilter user={initialParams.handleOrWallet} options={initialParams.logTypes} selected={selectedFilters} setSelected={onFilter} /></div>}</div>;
    $[56] = initialParams.handleOrWallet;
    $[57] = initialParams.logTypes;
    $[58] = matter;
    $[59] = onFilter;
    $[60] = onMatterChange;
    $[61] = selectedFilters;
    $[62] = targetType;
    $[63] = withFilters;
    $[64] = withMatterFilter;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  let t20;
  if ($[66] !== t16 || $[67] !== t17 || $[68] !== t18 || $[69] !== t19) {
    t20 = <div className={t16}>{t17}{t18}{t19}</div>;
    $[66] = t16;
    $[67] = t17;
    $[68] = t18;
    $[69] = t19;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  let t21;
  if ($[71] !== currentPage || $[72] !== initialParams.handleOrWallet || $[73] !== logs) {
    t21 = logs ? <div>{logs?.data.length ? <div className="tw-flow-root tw-scroll-py-3 tw-overflow-x-auto tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300"><ProfileActivityLogsList logs={logs.data} user={initialParams.handleOrWallet} /><CommonTablePagination currentPage={currentPage} setCurrentPage={setCurrentPage} totalPages={null} haveNextPage={logs.next} small={!!initialParams.handleOrWallet} showTopBorder={false} className="tw-pt-6" /></div> : <div className="tw-py-4"><span className={`${initialParams.handleOrWallet ? "tw-px-2 sm:tw-px-3" : ""} tw-text-sm tw-font-normal tw-italic tw-text-iron-500`}>No Activity Log</span></div>}</div> : <div className="tw-h-screen tw-w-full"><CommonCardSkeleton /></div>;
    $[71] = currentPage;
    $[72] = initialParams.handleOrWallet;
    $[73] = logs;
    $[74] = t21;
  } else {
    t21 = $[74];
  }
  let t22;
  if ($[75] !== t15 || $[76] !== t20 || $[77] !== t21) {
    t22 = <div className={t15}>{t20}{t21}</div>;
    $[75] = t15;
    $[76] = t20;
    $[77] = t21;
    $[78] = t22;
  } else {
    t22 = $[78];
  }
  return t22;
}